import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorBanner,
  useActionSlow,
  useToast,
} from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { TIMEOUT_ORDER_LONGAMS } from '@app/api/client';
import { useCanOperar } from '@app/auth/useCan';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import {
  useMaterializeAuthorization,
  useReconcileAdmission,
  useRuntimeAuthorization,
  type OrderResult,
} from './Runtime.api';
import { authorization, difference, keyTri } from './runtimeVocab';
import s from './Runtime.module.css';

/**
 * Card **Desejado × publicado (PDP)** de T15 (FR-092).
 *
 * A comparação é do tenant inteiro, não de uma identidade: o PDP recebe um
 * documento só. `não lido` é diferente de `em dia` — quando a leitura falha, a
 * tela **não** diz que está tudo certo.
 */

type Action = 'materializar' | 'reconciliar';

/**
 * As duas ordens do card. Só as chaves: o texto sai do catálogo no render, pelo
 * motivo de sempre (uma tabela de módulo congela o idioma da importação).
 */
const CONFIRMACAO: Record<Action, { titulo: string; mensagem: string; confirmar: string; espera: string }> = {
  materializar: {
    titulo: 'runtimeStatus.authorizationCard.materializar.title',
    mensagem: 'runtimeStatus.authorizationCard.materializar.message',
    confirmar: 'runtimeStatus.authorizationCard.materializar.confirm',
    espera: 'runtimeStatus.authorizationCard.republish',
  },
  reconciliar: {
    titulo: 'runtimeStatus.authorizationCard.reconciliar.title',
    mensagem: 'runtimeStatus.authorizationCard.reconciliar.message',
    confirmar: 'runtimeStatus.authorizationCard.reconciliar.confirm',
    espera: 'runtimeStatus.authorizationCard.reconcile',
  },
};

export function AuthorizationCard() {
  const autorizacao = useRuntimeAuthorization();
  const materialize = useMaterializeAuthorization();
  const reconcile = useReconcileAdmission();
  const toast = useToast();
  const wait = useActionSlow();

  // FR-010: sem permissão os botões não aparecem — nunca aparecem desabilitados.
  const canMaterialize = useCanOperar('materializeAuthorization');
  const canReconcile = useCanOperar('reconcileAdmission');

  const [acao, setAcao] = useState<Action | null>(null);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [resultado, setResultado] = useState<OrderResult | null>(null);

  const estado = authorization(keyTri(autorizacao.data?.up_to_date));
  const diferencas = autorizacao.data?.differences ?? [];
  const pendente = materialize.isPending || reconcile.isPending;

  async function confirm() {
    if (!acao) return;
    setErro(null);
    try {
      const mutation = acao === 'materializar' ? materialize : reconcile;
      // A ordem entra no indicador de tarefas da topbar (não bloqueante) e o
      // `ConfirmDialog` segue na frente com o botão em «…». Falhou: a tarefa sai
      // e o erro volta para o banner do próprio diálogo — sem mensagem repetida.
      const output = await wait.executar(() => mutation.mutateAsync(), {
        titulo: CONFIRMACAO[acao].espera,
        // Mesmo teto que as duas mutações deram ao axios (`Runtime.api.ts`).
        limiteMs: TIMEOUT_ORDER_LONGAMS,
      });
      setResultado(output);
      setAcao(null);
      toast.mostrar(
        t(
          acao === 'materializar'
            ? 'runtimeStatus.authorizationCard.republished'
            : 'runtimeStatus.authorizationCard.reconciled',
        ),
        {
        tone: 'sucesso',
      });
    } catch (falha) {
      setErro(falha as ApiError);
    }
  }

  return (
    <Card
      title={t('runtimeStatus.authorizationCard.title')}
      subtitle={
        autorizacao.data?.read_at
          ? t('runtimeStatus.page.meta.read', { when: dataHour(autorizacao.data.read_at) ?? '—' })
          : undefined
      }
      tip={t('runtimeStatus.authorizationCard.tip')}
    >
      {autorizacao.isError ? (
        <ErrorBanner
          error={autorizacao.error}
          title={t('runtimeStatus.authorizationCard.error')}
          onRetry={() => void autorizacao.refetch()}
        />
      ) : null}

      {autorizacao.isLoading ? <p className={s.carregando}>{t('runtimeStatus.authorizationCard.loading')}</p> : null}

      {autorizacao.data ? (
        <>
          <p className={s.estadoLinha}>
            <Badge tone={estado.tone}>{estado.rotulo}</Badge>
            {autorizacao.data.detail ? (
              <span className={s.estadoDetalhe}>{autorizacao.data.detail}</span>
            ) : null}
          </p>

          {diferencas.length === 0 ? (
            <p className={s.vazioTexto}>
              {t(
                autorizacao.data.up_to_date === true
                  ? 'runtimeStatus.authorizationCard.noDifference'
                  : 'runtimeStatus.authorizationCard.noDifferenceListed',
              )}
            </p>
          ) : (
            <ul className={s.diferencas}>
              {diferencas.map((diferenca, index) => (
                <li
                  key={`${diferenca.spiffe_id ?? index}-${diferenca.kind}`}
                  className={s.diferenca}
                >
                  <span className={s.nome}>{diferenca.application ?? '—'}</span>
                  <Badge tone="warning">{difference(diferenca.kind)}</Badge>
                  <span className={s.checkMotivo}>{diferenca.detail ?? '—'}</span>
                  {diferenca.spiffe_id ? (
                    <span className={s.sub} title={diferenca.spiffe_id}>
                      {diferenca.spiffe_id}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {/* As ordens ficam no rodapé do card, depois do que elas corrigem — na
          toolbar do cabeçalho, com o título longo e a coluna estreita, os dois
          botões quebravam para o meio da tela (item 9.1 do roteiro do PO, 28/08). */}
      {canMaterialize || canReconcile ? (
        <div className={s.acoesCard}>
          {canMaterialize ? (
            <Button variant="out" icon="refresh" onClick={() => setAcao('materializar')}>
              {t('runtimeStatus.authorizationCard.republish')}
            </Button>
          ) : null}
          {canReconcile ? (
            <Button variant="out" icon="shield" onClick={() => setAcao('reconciliar')}>
              {t('runtimeStatus.authorizationCard.reconcile')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {resultado ? (
        <ul className={s.passos} aria-label={t('runtimeStatus.authorizationCard.steps.label')}>
          {resultado.steps.map((step, index) => (
            <li key={`${step.target}-${index}`} className={s.passo}>
              <Badge tone={step.ok ? 'success' : 'danger'}>{step.target}</Badge>
              <span className={s.checkMotivo}>{step.detail ??
                t(
                  step.ok
                    ? 'runtimeStatus.authorizationCard.step.ok'
                    : 'runtimeStatus.authorizationCard.step.failed',
                )}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={acao !== null}
        title={acao ? t(CONFIRMACAO[acao].titulo) : ''}
        message={acao ? t(CONFIRMACAO[acao].mensagem) : ''}
        confirmLabel={
          acao ? t(CONFIRMACAO[acao].confirmar) : t('runtimeStatus.authorizationCard.confirm')
        }
        loading={pendente}
        error={erro ? <ErrorBanner error={erro} escrita title={t('runtimeStatus.authorizationCard.error.action')} /> : null}
        onConfirm={() => void confirm()}
        onCancel={() => {
          setAcao(null);
          setErro(null);
        }}
      />
    </Card>
  );
}
